import { Ban, CircleX } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/clientWeb/components/ui/dialog'
import { Button } from '@/clientWeb/components/ui/button'
import { useTranslation } from '@/clientWeb/i18n/LanguageContext'

export interface MeetingFullDialogProps {
  open: boolean
  /** Closed without choosing: they stay on the page they are on. */
  onClose: () => void
  onTryAgain: () => void
  /** To the dashboard, or for a guest, who has none, to sign-in. */
  onLeave: () => void
  isGuest: boolean
  /** A Try again is in flight. */
  retrying?: boolean
  /** The last Try again was refused too, so say so: the dialog looks unchanged otherwise. */
  stillFull?: boolean
}

/**
 * Every seat is taken (MEETING_FULL).
 *
 * Shown over the page the join was made from, which stays put underneath: a
 * seat can free up at any moment, and trying again from here is quicker than
 * finding the link again. Replaces a toast that sent everyone back to the
 * dashboard, and a guest to sign-in, with no way to try again.
 */
export default function MeetingFullDialog({
  open,
  onClose,
  onTryAgain,
  onLeave,
  isGuest,
  retrying = false,
  stillFull = false,
}: MeetingFullDialogProps) {
  const t = useTranslation()

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !retrying) onClose()
      }}
    >
      <DialogContent
        hideClose
        className="w-[95vw] sm:max-w-[460px] p-0 gap-0 overflow-hidden bg-card dark:bg-card border-0 shadow-2xl"
        style={{ borderRadius: '16px' }}
      >
        <DialogHeader className="px-5 py-4 sm:px-6 border-b border-stroke-100 dark:border-border">
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-white text-start">
              {t('meetings.full.title')}
            </DialogTitle>
            <button
              type="button"
              onClick={onClose}
              disabled={retrying}
              className="rounded-full p-1 text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
            >
              <CircleX className="h-6 w-6" />
              <span className="sr-only">{t('common.close')}</span>
            </button>
          </div>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 px-6 py-8 text-center">
          <Ban className="h-20 w-20 text-red-400" strokeWidth={1.75} aria-hidden="true" />
          <DialogDescription className="text-sm sm:text-base font-medium leading-relaxed text-slate-700 dark:text-foreground/90">
            {t('meetings.errors.MEETING_FULL')}
          </DialogDescription>
          {stillFull && !retrying && (
            <p role="status" className="text-xs text-muted-foreground">
              {t('meetings.full.stillFull')}
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 px-5 pb-5 sm:px-6 sm:pb-6">
          <Button
            type="button"
            variant="outline"
            onClick={onLeave}
            disabled={retrying}
            className="flex-1"
          >
            {isGuest ? t('meetings.full.goToSignIn') : t('meetings.full.goToDashboard')}
          </Button>
          <Button
            type="button"
            onClick={onTryAgain}
            disabled={retrying}
            className="flex-1 bg-primary hover:bg-primary/90 text-white"
          >
            {retrying ? t('meetings.full.trying') : t('meetings.full.tryAgain')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
